import { useQuery } from 'react-query';

import { ListProgramResourcesResponsesByAccept } from '@bestdoctor/core-arch-api/api/program/types';
import { getPagesQuantity } from 'utils/hooksHelpers';
import { Company, DataPeriod, SearchParams } from 'interfaces/global/globalInterfaces';
import { fetchProgramsPage } from 'hooks/programs/programsQuery';
import { PROGRAMS_CACHE_TIME, PROGRAMS_STALE_TIME } from 'hooks/programs/programsQueryCache';

interface SearchProgramsForStaffFilterHookParams {
  periods: DataPeriod[] | null;
  periodInMainFilter: DataPeriod | null;
  companies: Company[];
  isSlotsAviable: boolean;
  onError: (res: { response: Response }) => void;
}

interface SearchProgramsForStaffFilterHookData {
  isLoadingGetProgramsForFilter: boolean;
  isErrorGetProgramsForFilter: boolean;

  fetchNextPage: Promise<void>;

  programsData: SearchParams[];
}

export const useSearchProgramsForStaffFilter = ({
  periods,
  periodInMainFilter,
  companies,
  isSlotsAviable,
  onError,
}: SearchProgramsForStaffFilterHookParams): SearchProgramsForStaffFilterHookData => {
  const activePeriodsUuids = periods
    ?.reduce((acc: string[], item: DataPeriod) => {
      if (item.activePeriod) {
        acc.push(item.uuid);
      }
      return acc;
    }, [])
    .join(',');

  const servicePeriodIn =
    companies.length > 1 ? activePeriodsUuids || '' : companies.length === 1 ? periodInMainFilter?.uuid || '' : '';

  const {
    isLoading,
    error,
    fetchNextPage,

    data: programsData,
  } = useQuery(
    ['dataProgram', activePeriodsUuids, periodInMainFilter],
    () =>
      fetchProgramsPage(
        {
          companyUuid: '',
          periodId: '',
          servicePeriodIn,
          programId: '',
          valuesServices: '',
          cityId: '',
          isAttachProgramsQuery: companies.length >= 1 && !!servicePeriodIn,
        },
        1
      ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: companies.length > 1 ? !!activePeriodsUuids && isSlotsAviable : !!periodInMainFilter && isSlotsAviable,
      staleTime: PROGRAMS_STALE_TIME,
      cacheTime: PROGRAMS_CACHE_TIME,
      getNextPageParam: (lastPage: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr']) => {
        const allPage: number = getPagesQuantity(lastPage);
        const currentPage: number = lastPage.meta?.page || 0;
        const nextPage = currentPage + 1;

        return nextPage <= allPage ? nextPage : undefined;
      },
      onError,
      select: (res: { data: Array<{ title: string; uuid: string }> }) => {
        const { data } = res;

        const groupedPrograms = data.reduce<Record<string, string[]>>((acc, val) => {
          const key = val.title;
          if (!acc[key]) {
            acc[key] = [val.uuid];
          } else {
            acc[key].push(val.uuid);
          }
          return acc;
        }, {});

        const programs = Object.entries(groupedPrograms).map(([title, value]) => {
          const valueStr = (value as string[]).join(',');
          return {
            id: valueStr,
            title,
            value: valueStr,
          };
        });

        return programs;
      },
    }
  );

  return {
    isLoadingGetProgramsForFilter: isLoading,
    isErrorGetProgramsForFilter: error,

    fetchNextPage,

    programsData,
  };
};
